import { Button } from '@/components/Button/Button'
import { Input } from '@/components/Input/Input'
import { Loader } from '@/components/Loader/Loader'
import { Popup } from '@/components/Popup/Popup'
import { TextArea } from '@/components/TextArea/TextArea'
import { GeneratorFormContext } from '@/hooks/useGeneratorForm'
import { usePresetsState } from '@/state/presets'
import type { FunctionalComponent } from 'preact'
import { useContext, useState } from 'preact/hooks'

const placeholder = `Describe the post structure and what information it should include.`

export const PresetForm: FunctionalComponent<{
  type: 'create' | 'edit'
  onSuccess: () => void
  onCancel?: () => void
  onDelete?: () => void
}> = props => {
  const createPreset = usePresetsState(s => s.create)
  const updatePreset = usePresetsState(s => s.update)
  const [loading, setLoading] = useState(false)

  const form = useContext(GeneratorFormContext)!
  const preset = usePresetsState(s => s.list.find(item => item.id === form.presetId))

  const [name, setName] = useState(props.type === 'edit' ? (preset?.name ?? '') : '')
  const [content, setContent] = useState(props.type === 'edit' ? (preset?.content ?? '') : '')

  const submit = async () => {
    try {
      setLoading(true)
      let presetId: number
      if (props.type === 'create') {
        const data = await createPreset(name, content)
        presetId = data.id
      }
      else {
        if (!form.presetId) throw new Error('Preset id myst be defined')
        const data = await updatePreset(form.presetId, name, content)
        presetId = data.id
      }
      form.setPresetId(presetId)
      props.onSuccess()
    }
    catch (e) {
      console.error(e)
    }
    finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Popup.Title>
        {props.type === 'create' ? 'Create post type' : 'Edit post type'}
      </Popup.Title>

      <Popup.Fields>
        {props.type === 'create' && (
          <Popup.Msg>
            Define the format of this post. Describe what kind of update it is and how it should be
            structured.
          </Popup.Msg>
        )}

        <Popup.Field label='Name'>
          <Input
            placeholder='My post type'
            disabled={loading}
            value={name}
            onChange={setName}
          />
        </Popup.Field>

        <Popup.Field label='Instructions'>
          <TextArea
            minRows={5}
            placeholder={placeholder}
            disabled={loading}
            value={content}
            onChange={setContent}
          />
        </Popup.Field>
      </Popup.Fields>

      <Popup.Footer>
        <Popup.Buttons>
          {props.type === 'edit' && preset && preset.id > -1 && (
            preset.defaultPresetId !== null
              ? (
                <Button onClick={props.onDelete}>
                  Reset
                </Button>
              )
              : (
                <Button onClick={props.onDelete}>
                  Delete
                </Button>
              )
          )}
        </Popup.Buttons>

        <Popup.Buttons>
          {props.onCancel && (
            <Button
              onClick={props.onCancel}
            >
              Back
            </Button>
          )}
          <Button
            type='primary'
            disabled={loading || !name.trim() || !content.trim()}
            onClick={submit}
          >
            {props.type === 'edit' ? 'Save' : 'Create'}
            {loading && (
              <>
                {' '}
                <Loader />
              </>
            )}
          </Button>
        </Popup.Buttons>
      </Popup.Footer>
    </>
  )
}
